import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "jobSelect", "newProjectSection", "existingProjectSection", "existingProjectHelp",
    "titleField", "descriptionField", "clientOrgField",
    "dropzoneTarget", "dropzoneTemplate", "dropzonePreviews",
    "smsMessage", "msgForm", "msgComplete", "smsModal", "counter",
    "contactForm", "submitButton"
  ]
  
  static values = { 
    uploadsPath: String,
    supplierId: String,
    eligibilityCheckUrl: String
  }

  // Constants
  static ALERT_TYPES = {
    INFO: { class: 'alert-info', icon: 'fa-info-circle' },
    WARNING: { class: 'alert-warning', icon: 'fa-exclamation-triangle' },
    LOADING: { class: 'alert-info', icon: 'fa-spinner fa-spin' }
  }

  connect() {
    this.storeOriginalHelpText()
    this.initializeAllComponents()
  }
  
  storeOriginalHelpText() {
    if (this.hasExistingProjectHelpTarget) {
      this.originalHelpText = this.existingProjectHelpTarget.innerHTML
    }
  }

  initializeAllComponents() {
    const components = [
      'initializeSelectize',
      'initializeVisibility', 
      'initializeDropzone',
      'initializeSMS'
    ]
    
    components.forEach(method => {
      try {
        this[method]()
      } catch (error) {
        console.warn(`Failed to ${method.replace('initialize', '').toLowerCase()}:`, error)
      }
    })
  }

  initializeSelectize() {
    if (typeof $ !== 'undefined' && this.hasJobSelectTarget) {
      $(this.jobSelectTarget).selectize({
        maxItems: 1,
        sortField: "text",
        create: false,
        required: true,
        onChange: (value) => this.handleJobSelectChange(value)
      })
    }
  }

  initializeVisibility() {
    this.toggleSections({ showNew: false, showExisting: true })
    this.setRequiredFields(false)
  }

  initializeDropzone() {
    if (this.hasDropzoneComponents() && typeof $.uf !== 'undefined') {
      $.uf.dropzoneS3(
        this.dropzoneTargetTarget, 
        this.dropzoneTemplateTarget,
        this.dropzonePreviewsTarget, 
        this.uploadsPathValue,
        { maxFiles: null, clickable: ".fileinput-button" }
      )
    }
  }

  initializeSMS() {
    if (typeof $ === 'undefined' || typeof $.uf === 'undefined') return

    this.setupSMSMaxLength()
    this.setupSMSFormHandlers()
    this.setupSMSModalHandlers()
  }

  hasDropzoneComponents() {
    return typeof $ !== 'undefined' && 
           this.hasDropzoneTargetTarget && 
           this.hasDropzoneTemplateTarget && 
           this.hasDropzonePreviewsTarget
  }

  setupSMSMaxLength() {
    if (this.hasSmsMessageTarget) {
      $.uf.maxLength(".sms-message", "textarea")
    }
  }

  setupSMSFormHandlers() {
    if (this.hasMsgFormTarget) {
      $(this.msgFormTarget)
        .on("submit", () => this.toggleSMSViews(false))
        .bind('ajax:complete', () => $(this.msgFormTarget).trigger("reset"))
    }
  }

  setupSMSModalHandlers() {
    if (this.hasSmsModalTarget) {
      $(this.smsModalTarget).on('hidden.bs.modal', () => {
        if (this.hasCounterTarget) {
          $(this.counterTarget).html(160)
        }
        this.toggleSMSViews(true)
      })
    }
  }

  jobSelectChanged(event) {
    this.handleJobSelectChange(event.target.value)
  }

  handleJobSelectChange(selectedValue) {
    const actions = {
      'new': () => this.handleNewProject(),
      'existing': () => this.handleExistingProject(selectedValue),
      'default': () => this.handleDefaultSelection()
    }

    const actionKey = selectedValue === "new" ? 'new' : 
                     (selectedValue && selectedValue !== "" && selectedValue !== "Select...") ? 'existing' : 'default'
    
    actions[actionKey]()
  }

  handleNewProject() {
    this.toggleSections({ showNew: true, showExisting: false })
    this.setRequiredFields(true)
    this.enableForm()
  }

  handleExistingProject(selectedValue) {
    this.toggleSections({ showNew: false, showExisting: true })
    this.setRequiredFields(false)
    this.checkJobEligibility(selectedValue)
  }

  handleDefaultSelection() {
    this.toggleSections({ showNew: false, showExisting: true })
    this.setRequiredFields(false)
    this.enableForm()
  }

  toggleSections({ showNew, showExisting }) {
    this.toggleElementVisibility(this.newProjectSectionTargets, showNew)
    this.toggleElementVisibility(this.existingProjectSectionTargets, showExisting)
  }

  toggleElementVisibility(elements, show) {
    if (elements) {
      elements.forEach(element => element.style.display = show ? 'block' : 'none')
    }
  }

  setRequiredFields(required) {
    const fields = ['titleField', 'descriptionField', 'clientOrgField']
    
    fields.forEach(fieldName => {
      const targetName = `${fieldName}Target`
      if (this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}`]) {
        const element = this[targetName]
        required ? element.setAttribute('required', 'required') : element.removeAttribute('required')
      }
    })
  }

  enableForm() {
    this.setFormState(true)
    this.restoreOriginalHelpText()
  }

  disableForm() {
    this.setFormState(false)
  }

  setFormState(enabled) {
    if (!this.hasContactFormTarget) return

    const inputs = this.contactFormTarget.querySelectorAll('input, select, textarea, button[type="submit"]')
    
    inputs.forEach(input => {
      if (input === this.jobSelectTarget) return // Always keep job select enabled
      
      input.disabled = !enabled
      input.style.opacity = enabled ? '1' : '0.6'
    })

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !enabled
      this.submitButtonTarget.style.opacity = enabled ? '1' : '0.6'
    }

    // Restore form-level styles when enabling
    if (enabled && this.hasContactFormTarget) {
      this.contactFormTarget.style.opacity = '1'
      this.contactFormTarget.style.pointerEvents = 'auto'
    }
  }

  toggleSMSViews(showForm) {
    if (this.hasMsgFormTarget) {
      this.msgFormTarget.style.display = showForm ? 'block' : 'none'
    }
    if (this.hasMsgCompleteTarget) {
      this.msgCompleteTarget.style.display = showForm ? 'none' : 'block'
    }
  }

  async checkJobEligibility(jobId) {
    if (!this.supplierIdValue || !this.eligibilityCheckUrlValue) {
      console.error("Missing supplier ID or eligibility check URL")
      return
    }

    try {
      this.showAlertInHelpText('LOADING', window.I18n["views.suppliers.contact.form.ineligibility.checking"])
      
      const response = await fetch(`${this.eligibilityCheckUrlValue}?job_id=${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content')
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.eligible) {
        this.enableForm()
      } else {
        this.disableForm()
        this.showAlertInHelpText('INFO', data.message)
      }
    } catch (error) {
      console.error('Error checking job eligibility:', error)
      this.enableForm()
    }
  }

  showAlertInHelpText(type, message) {
    if (!this.hasExistingProjectHelpTarget) return

    const alertConfig = this.constructor.ALERT_TYPES[type]
    const iconClass = type === 'LOADING' ? `${alertConfig.icon}` : alertConfig.icon
    
    this.existingProjectHelpTarget.innerHTML = `
      <div class="alert ${alertConfig.class}" style="margin-top: 10px;">
        <i class="fa ${iconClass}"></i> ${message}
      </div>
    `
    this.existingProjectHelpTarget.className = 'help-block existing-project-visible'
  }

  restoreOriginalHelpText() {
    if (this.hasExistingProjectHelpTarget && this.originalHelpText) {
      this.existingProjectHelpTarget.innerHTML = this.originalHelpText
      this.existingProjectHelpTarget.className = 'help-block existing-project-visible'
    }
  }
}